<!-- src/views/admin/AdminManageRolesView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] p-4 sm:p-6 md:p-8 relative selection:bg-[#20FCF1]/30 font-sans text-left">
    <div class="max-w-7xl mx-auto pb-24">

      <!-- HEADER & ACTIONS: Midnight Premium Standard -->
      <div class="flex flex-col xl:flex-row items-start xl:items-center justify-between mb-12 gap-6 bg-[#0A1630] p-10 rounded-[40px] shadow-2xl border-b-4 border-[#20FCF1] animate-fadeIn">
        <div>
          <h1 class="text-3xl font-black text-white uppercase tracking-tight">Security Protocols</h1>
          <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-2">Central Management Portal • Sentinel Safe Premium</p>
        </div>
        
        <div class="flex flex-wrap gap-3 w-full xl:w-auto items-center">
            <!-- 🛡️ FIXED: Corrected route name to align with AdminPersonnelDashboard -->
            <router-link :to="{ name: 'AdminPersonnelDashboard' }" class="px-8 py-4 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2">
               &larr; Back to Directory
            </router-link>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-10">
        
        <!-- Left Column: Registry of Roles -->
        <div class="lg:col-span-2 bg-white rounded-[40px] shadow-xl border border-slate-100 overflow-hidden">
          <div class="p-8 border-b border-slate-50 bg-[#0A1630]">
            <h2 class="text-xs font-black text-[#20FCF1] uppercase tracking-[0.3em]">Authorised Access Levels</h2>
          </div>

          <div class="p-4 sm:p-8">
            <div v-if="loadingRoles" class="text-center py-20">
              <div class="inline-block animate-spin rounded-full h-10 w-10 border-4 border-[#20FCF1] border-t-transparent mb-4"></div>
              <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Decrypting Policy Registry...</p>
            </div>

            <div v-else-if="rolesError" class="bg-rose-50 border border-rose-100 text-rose-700 p-6 rounded-2xl text-xs font-bold uppercase text-center tracking-widest mb-6">
              Registry Access Error: {{ rolesError }}
            </div>

            <div v-else-if="roles.length === 0" class="text-center py-20 bg-slate-50 rounded-[32px] border border-dashed border-slate-200">
              <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">No custom policies defined.</p>
            </div>

            <ul v-else class="divide-y divide-slate-50">
              <li v-for="role in roles" :key="role.id" class="py-6 flex justify-between items-center group">
                <div class="flex items-center gap-5">
                  <div class="p-3 rounded-2xl bg-slate-50 border border-slate-100 transition-colors group-hover:bg-[#0A1630]">
                    <component 
                      :is="getRoleIconDetails(role.name).icon" 
                      :class="getRoleIconDetails(role.name).color" 
                      class="h-6 w-6 flex-shrink-0 transition-colors group-hover:text-[#FD5E00]" 
                    />
                  </div>
                  <div>
                    <span class="text-[#0A1630] font-black uppercase text-sm tracking-tight block">{{ role.name }}</span>
                    <span v-if="role.isDefault" class="mt-1 inline-block text-[8px] font-black bg-[#EBFDF8] text-[#20FCF1] px-2 py-0.5 rounded-full uppercase tracking-widest border border-[#20FCF1]/20">System Default</span>
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  <button 
                    @click="startEditing(role)" 
                    :disabled="role.isDefault"
                    class="px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-sm"
                    :class="role.isDefault ? 'bg-slate-50 text-slate-300 cursor-not-allowed' : 'bg-slate-100 text-slate-600 hover:bg-[#0A1630] hover:text-white'"
                  >
                    Modify
                  </button>
                  <button 
                    v-if="!role.isDefault" 
                    @click="deleteRole(role)" 
                    :disabled="isDeleting === role.id" 
                    class="px-6 py-2.5 bg-rose-50 text-rose-500 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-rose-500 hover:text-white transition-all shadow-sm"
                  >
                    <span v-if="isDeleting === role.id" class="flex items-center gap-1"><div class="h-2 w-2 bg-white rounded-full animate-pulse"></div></span>
                    <span v-else>Purge</span>
                  </button>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- Right Column: Governance Form -->
        <div class="bg-white p-10 rounded-[40px] shadow-2xl border border-slate-100 flex flex-col h-fit sticky top-8">
          <div class="mb-10">
            <!-- Terminology Fix: Role Management -->
            <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight text-left">
              {{ isEditing ? 'Edit Protocol' : 'Role Management' }}
            </h2>
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1 text-left">Define permission parameters</p>
          </div>

          <form @submit.prevent="handleSaveRole" class="space-y-10">
            <div class="text-left">
              <label class="micro-label mb-3 block ml-1">Identity Tag</label>
              <input 
                type="text" 
                id="roleName" 
                v-model="roleName" 
                required 
                class="form-input" 
                placeholder="e.g., SITE SUPERVISOR" 
                :disabled="isEditing && editingRoleIsDefault"
              />
              <p v-if="isEditing && editingRoleIsDefault" class="text-[9px] font-black text-slate-400 mt-2 uppercase tracking-widest flex items-center gap-1">
                <ShieldCheckIcon class="h-3 w-3" /> System Restricted Label
              </p>
            </div>
            
            <div class="pt-8 border-t border-slate-50 text-left">
                <div class="flex items-center justify-between mb-6">
                  <h3 class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest">Authorisation Scope</h3>
                  <div v-if="isEditing && editingRoleIsDefault" class="bg-amber-50 text-amber-600 px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest">Read Only</div>
                </div>

                <div class="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                    <div 
                      v-for="permission in availablePermissions" 
                      :key="permission.id" 
                      class="flex items-center p-3 rounded-xl border border-transparent transition-colors hover:bg-slate-50"
                    >
                        <div class="relative flex items-center">
                          <input 
                              :id="permission.id" 
                              type="checkbox" 
                              v-model="editingPermissions[permission.id]"
                              class="h-5 w-5 rounded-lg border-slate-200 text-[#20FCF1] focus:ring-[#20FCF1] cursor-pointer"
                              :disabled="isEditing && editingRoleIsDefault"
                          >
                        </div>
                        <label :for="permission.id" class="ml-4 block text-xs font-bold text-slate-600 uppercase tracking-tight cursor-pointer selection:bg-transparent">
                          {{ permission.label }}
                        </label>
                    </div>
                </div>
            </div>
            
            <div class="flex flex-col gap-3 pt-6">
              <button type="submit" :disabled="isSaving" class="btn-premium">
                <span v-if="isSaving" class="flex items-center gap-2">Syncing Data...</span>
                <span v-else>{{ isEditing ? 'Update Policy' : 'Authorise Protocol' }}</span>
              </button>
              <button v-if="isEditing" @click="cancelEditing" type="button" class="w-full py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors">
                Discard Changes
              </button>
            </div>
            <p v-if="formError" class="text-[10px] text-rose-600 font-black uppercase text-center mt-4">{{ formError }}</p>
            <!-- Sync Hint -->
            <p v-if="showRefreshHint" class="text-[9px] text-[#22C55E] font-black uppercase text-center mt-4 animate-pulse">
              Update Successful. Assigned users must refresh their Dashboard to synchronise access.
            </p>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '@/firebaseConfig';
import { doc, deleteDoc, setDoc, collection, getDocs } from 'firebase/firestore'; 
import { useUserStore } from '@/stores/userStore';
import { 
  ShieldCheckIcon, UserCircleIcon, UserGroupIcon, 
  BoltIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const functions = getFunctions(auth.app, 'europe-west2');
const roles = ref([]);
const loadingRoles = ref(true);
const rolesError = ref(null);

const roleName = ref('');
const formError = ref(null);
const isSaving = ref(false);
const isDeleting = ref(null);
const showRefreshHint = ref(false);

const isEditing = ref(false);
const editingRoleId = ref(null);
const editingPermissions = ref({});

const editingRoleIsDefault = computed(() => {
    if (!isEditing.value || !editingRoleId.value) return false;
    const role = roles.value.find(r => r.id === editingRoleId.value);
    return role ? role.isDefault : false;
});

/** 🛡️ ALIGNED PERMISSIONS MATRIX
 * Obsolete "Report System Issue" and "Request Feature" options removed as requested.
 */
const availablePermissions = ref([
    { id: 'canViewUsers', label: 'Personnel Command Centre' },
    { id: 'canViewOnboarding', label: 'Onboarding Command Centre' },
    { id: 'canViewHealthAndSafety', label: 'Health & Safety Command Centre' },
    { id: 'canViewFatigue', label: 'Fatigue Command Centre' },
    { id: 'canViewEngineering', label: 'Engineering Command Centre' },
    { id: 'canViewTraining', label: 'Training Command Centre' },
    { id: 'canViewVehicles', label: 'Fleet Command Centre' },
    { id: 'canViewFinance', label: 'Financial Command Centre' },
    { id: 'canManagePoliciesAndProcedures', label: 'Document Command Centre' },
    { id: 'canViewResources', label: 'Resource Command Centre' },
    { id: 'canViewTasks', label: 'Task Command Centre' },
    { id: 'canViewAnalytics', label: 'Intelligence & Analytics' }
]);

const getRoleIconDetails = (roleName) => {
  const r = (roleName || '').toLowerCase();
  if (r.includes('admin')) {
    return { icon: ShieldCheckIcon, color: 'text-[#20FCF1]' };
  }
  if (r.includes('manager')) {
    return { icon: UserGroupIcon, color: 'text-indigo-400' };
  }
  if (r === 'standard') {
    return { icon: UserCircleIcon, color: 'text-slate-400' };
  }
  return { icon: BoltIcon, color: 'text-amber-400' };
};

const resetPermissions = () => {
    const newPermissions = {};
    availablePermissions.value.forEach(p => {
        newPermissions[p.id] = false;
    });
    editingPermissions.value = newPermissions;
};

const fetchRoles = async () => {
  loadingRoles.value = true;
  rolesError.value = null;
  try {
    const clientId = userStore.clientId || userStore.userProfile?.clientId;
    if (!clientId) throw new Error("Client Identity Missing.");

    // 🛡️ DIRECT FIRESTORE RETRIEVAL BYPASS: Resolves Super Admin server-side claim bottlenecks
    const rolesRef = collection(db, 'clients', clientId, 'permissionRoles');
    const snap = await getDocs(rolesRef);
    
    const fetchedRoles = snap.docs.map(d => ({
      id: d.id,
      ...d.data()
    }));

    // Add default system roles matching exactly what the backend mapping expects
    const systemDefaults = [
      { id: 'admin', name: 'Admin', isDefault: true, permissions: { canViewUsers: true, canViewOnboarding: true, canViewHealthAndSafety: true, canViewFatigue: true, canViewEngineering: true, canViewTraining: true, canViewVehicles: true, canViewFinance: true, canManagePoliciesAndProcedures: true, canViewResources: true, canViewTasks: true, canViewAnalytics: true } },
      { id: 'standard', name: 'Standard', isDefault: true, permissions: {} }
    ];

    roles.value = [...systemDefaults, ...fetchedRoles];
  } catch (err) {
    console.warn("Direct Firestore roles fetch failed, trying Cloud Function fallback...", err);
    try {
      const getRolesFunction = httpsCallable(functions, 'getPermissionRoles');
      const result = await getRolesFunction();
      roles.value = result.data || [];
    } catch (fallbackErr) {
      console.error("All role retrieval channels exhausted:", fallbackErr);
      rolesError.value = fallbackErr.message || "Failed to load roles.";
    }
  } finally {
    loadingRoles.value = false;
  }
};

onMounted(fetchRoles);

const handleSaveRole = async () => {
  if (!roleName.value.trim()) {
    formError.value = "Identity tag required.";
    return;
  }

  isSaving.value = true;
  formError.value = null;
  showRefreshHint.value = false;

  try {
    const clientId = userStore.clientId || userStore.userProfile?.clientId;
    if (!clientId) throw new Error("Client Identity Missing.");

    const payload = {
      name: roleName.value.trim(),
      permissions: editingPermissions.value
    };

    if (isEditing.value) {
      if (editingRoleIsDefault.value) {
          throw new Error("System default policies are restricted.");
      }
      
      // 🛡️ DIRECT FIRESTORE UPDATE: Bypass empty Cloud Function stubs
      const roleRef = doc(db, 'clients', clientId, 'permissionRoles', editingRoleId.value);
      await setDoc(roleRef, payload, { merge: true });
    } else {
      // 🛡️ DIRECT FIRESTORE CREATE: Generate deterministic role document ID
      const newRoleRef = doc(collection(db, 'clients', clientId, 'permissionRoles'));
      await setDoc(newRoleRef, payload);
    }

    alert("Access Governance Policy Synchronised successfully.");
    cancelEditing(); 
    await fetchRoles(); 
    showRefreshHint.value = true;
    setTimeout(() => {
        showRefreshHint.value = false;
    }, 3000);
  } catch (e) {
    alert("Save Failure: " + e.message);
  } finally {
    isSaving.value = false;
  }
};

const deleteRole = async (role) => {
  if (role.isDefault) {
      alert("Default roles cannot be deleted.");
      return;
  }
  if (!confirm(`Confirm: Purge policy "${role.name}" from the registry?`)) {
    return;
  }
  
  isDeleting.value = role.id;
  
  try {
    const deleteRoleFunction = httpsCallable(functions, 'deletePermissionRole');
    await deleteRoleFunction({ roleId: role.id });
    await fetchRoles();
  } catch (err) {
    console.warn("Cloud Function Purge Failed. Initialising Direct Firestore Handshake...", err);
    
    try {
        const clientId = userStore.clientId || userStore.userProfile?.clientId;
        if (!clientId) throw new Error("Client Identity Missing.");
        
        const roleRef = doc(db, 'clients', clientId, 'permissionRoles', role.id);
        await deleteDoc(roleRef);
        
        await fetchRoles();
    } catch (directErr) {
        console.error("Registry Purge Failure:", directErr);
        alert("CRITICAL ERROR: Failed to purge policy from database.");
    }
  } finally {
    isDeleting.value = null;
  }
};

const startEditing = (role) => {
  isEditing.value = true;
  editingRoleId.value = role.id;
  roleName.value = role.name;
  
  const newPermissions = {};
    availablePermissions.value.forEach(p => {
        newPermissions[p.id] = role.permissions ? (role.permissions[p.id] || false) : false;
    });
    editingPermissions.value = newPermissions;
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const cancelEditing = () => {
  isEditing.value = false;
  editingRoleId.value = null;
  roleName.value = '';
  formError.value = null;
  resetPermissions();
};

resetPermissions();
</script>

<style scoped>
.micro-label { @apply text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]; }
.form-input { @apply block w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-black uppercase transition-all focus:ring-2 focus:ring-[#20FCF1] outline-none shadow-inner; }
.btn-premium { @apply w-full bg-[#20FCF1] text-[#0A1630] py-5 rounded-[24px] text-xs font-black uppercase tracking-[0.2em] shadow-xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:grayscale flex justify-center items-center; }
.animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #E2E8F0; border-radius: 10px; }
.custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #20FCF1; }
</style>